import { useMemo } from 'react';
import { User } from '../../types/User';
import { USER_MANAGEMENT_BASE_URL } from '../consts';
import { UsersFilters } from '../../types/filters';
import { QueryParams, usePaginatedGet, UsePaginatedGetParams, UsePaginatedGetReturnType } from '../../../../api';

interface Params {
    filters: UsersFilters;
}

type ReturnType = Omit<UsePaginatedGetReturnType<unknown>, 'data'> & {
    users?: User[];
};

type UserDto = { [Key in keyof User]: User[Key] extends Date ? Exclude<User[Key], Date> | string : User[Key] };

const NUMBER_OF_USERS_TO_FETCH = 100;

const convertFiltersToQueryParams = (filters: UsersFilters): UsePaginatedGetParams['queryParams'] => {
    return Object.entries(filters).reduce<UsePaginatedGetParams['queryParams']>(
        (queryParams: QueryParams = {}, [paramKey, paramValue]) => ({
            ...queryParams,
            ...(paramValue.length && {
                [paramKey]: paramValue.toString()
            })
        }),
        {}
    );
};

const convertUserDtoToUser = (userResponse: UserDto): User => {
    const { lastAccess, excludeTill, createDate, updateDate, ...restOfValues } = userResponse;

    return {
        ...restOfValues,
        createDate: new Date(userResponse.createDate),
        updateDate: new Date(userResponse.updateDate),
        ...(lastAccess && { lastAccess: new Date(lastAccess) }),
        ...(excludeTill && { excludeTill: new Date(excludeTill) }),
        tokens: userResponse.tokens.map((token) => ({
            ...token,
            createDate: new Date(token.createDate),
            lastUsed: token.lastUsed ? new Date(token.lastUsed) : undefined
        }))
    };
};

export const useGetUsers = ({ filters }: Params): ReturnType => {
    const { data: usersResponse, ...restOfReturnValues } = usePaginatedGet<UserDto>({
        url: `${USER_MANAGEMENT_BASE_URL}/protectedUsers`,
        limit: NUMBER_OF_USERS_TO_FETCH,
        queryParams: convertFiltersToQueryParams(filters)
    });

    const users = useMemo(() => usersResponse.map(convertUserDtoToUser), [usersResponse]);

    return {
        ...restOfReturnValues,
        users
    };
};
